/**
 * DebugMazeScene
 *
 * Standalone view for evaluating generator output. Renders the entire 24x32
 * playfield at a fixed debug tile size, with overlays for entities, spawn,
 * and the in-game viewport outline.
 *
 * Controls:
 *   R         new random seed
 *   T         today's daily seed (UTC date)
 *   SPACE     regenerate with the same seed (verify determinism)
 *   V         toggle viewport outline
 *   tap maze  same as R
 *
 * Color legend (visible in the debug overlay):
 *   dark green   ground
 *   near-black   foliage (blocks movement)
 *   blue         water (kills on contact)
 *   black dot    spider; thin line behind shows patrol path
 *   brown dot    coconut
 *   faded brown  invisible coconut (dashed outline)
 *   pink dot     UNREACHABLE coconut (bug — should not occur)
 *   gold circle  chimp spawn
 *   white rect   initial viewport (9x13) centered on spawn
 */

import Phaser from 'phaser';
import {
  PLAYFIELD_TILES_W,
  PLAYFIELD_TILES_H,
  VIEWPORT_PX_W,
  VIEWPORT_PX_H,
  VIEWPORT_TILES_W,
  VIEWPORT_TILES_H,
} from '../config';
import { generatePlayfield } from '../maze/generator';
import { Tile, type Playfield } from '../maze/types';

const HEADER_H = 38;
const FOOTER_H = 22;
const MAZE_AREA_H = VIEWPORT_PX_H - HEADER_H - FOOTER_H;

const DEBUG_TILE = Math.min(
  Math.floor(VIEWPORT_PX_W / PLAYFIELD_TILES_W),
  Math.floor(MAZE_AREA_H / PLAYFIELD_TILES_H)
);
const MAZE_W = PLAYFIELD_TILES_W * DEBUG_TILE;
const MAZE_H = PLAYFIELD_TILES_H * DEBUG_TILE;
const MAZE_X = Math.floor((VIEWPORT_PX_W - MAZE_W) / 2);
const MAZE_Y = HEADER_H;

const C = {
  ground: 0x2a3e1f,
  groundAlt: 0x253a1a,
  foliage: 0x081807,
  water: 0x1c4a7a,
  spider: 0x111111,
  spiderPatrol: 0x555555,
  coconut: 0xb37a3a,
  coconutInvisible: 0x6b3d20,
  coconutUnreachable: 0xff5577,
  chimp: 0xe8c87a,
  viewport: 0xffffff,
} as const;

export class DebugMazeScene extends Phaser.Scene {
  private playfield!: Playfield;
  private gridGfx!: Phaser.GameObjects.Graphics;
  private entityGfx!: Phaser.GameObjects.Graphics;
  private viewportGfx!: Phaser.GameObjects.Graphics;
  private headerText!: Phaser.GameObjects.Text;
  private statsText!: Phaser.GameObjects.Text;
  private viewportVisible = true;

  constructor() {
    super({ key: 'DebugMaze' });
  }

  create(): void {
    this.gridGfx = this.add.graphics();
    this.entityGfx = this.add.graphics();
    this.viewportGfx = this.add.graphics();

    this.headerText = this.add.text(8, 4, '', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#d9d9d9',
    });

    this.statsText = this.add.text(8, 21, '', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#8a8a8a',
    });

    this.add.text(8, VIEWPORT_PX_H - FOOTER_H + 5, 'R rand  T today  SPACE refresh  V viewport  ~ game', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#6a6a6a',
    });

    // Keyboard
    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-R', () => this.regenerate(randomSeed()));
      kb.on('keydown-T', () => this.regenerate(todaysSeed()));
      kb.on('keydown-SPACE', () => this.regenerate(this.playfield.seed));
      kb.on('keydown-V', () => {
        this.viewportVisible = !this.viewportVisible;
        this.drawViewport();
      });
      kb.on('keydown', (ev: KeyboardEvent) => {
        if (ev.key === '`' || ev.key === '~' || ev.key === 'Escape') {
          this.scene.start('Menu');
        }
      });
    }

    // Tap in the maze area for a new random seed.
    this.input.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      if (
        ptr.y >= MAZE_Y &&
        ptr.y <= MAZE_Y + MAZE_H &&
        ptr.x >= MAZE_X &&
        ptr.x <= MAZE_X + MAZE_W
      ) {
        this.regenerate(randomSeed());
      }
    });

    this.regenerate(todaysSeed());
  }

  private regenerate(seed: string): void {
    this.playfield = generatePlayfield(seed);
    this.draw();
  }

  private draw(): void {
    const pf = this.playfield;
    this.gridGfx.clear();
    this.entityGfx.clear();

    // Terrain tiles
    for (let y = 0; y < pf.height; y++) {
      for (let x = 0; x < pf.width; x++) {
        const t = pf.tiles[y * pf.width + x]!;
        let color: number;
        if (t === Tile.Foliage) color = C.foliage;
        else if (t === Tile.Water) color = C.water;
        else color = (x + y) % 2 === 0 ? C.ground : C.groundAlt;

        this.gridGfx.fillStyle(color, 1);
        this.gridGfx.fillRect(
          MAZE_X + x * DEBUG_TILE,
          MAZE_Y + y * DEBUG_TILE,
          DEBUG_TILE,
          DEBUG_TILE
        );
      }
    }

    // Spider patrol paths + spider markers
    for (const sp of pf.spiders) {
      const startCx = MAZE_X + sp.start.x * DEBUG_TILE + DEBUG_TILE / 2;
      const startCy = MAZE_Y + sp.start.y * DEBUG_TILE + DEBUG_TILE / 2;
      const endX =
        sp.axis === 'horizontal' ? sp.start.x + sp.length - 1 : sp.start.x;
      const endY =
        sp.axis === 'vertical' ? sp.start.y + sp.length - 1 : sp.start.y;
      const endCx = MAZE_X + endX * DEBUG_TILE + DEBUG_TILE / 2;
      const endCy = MAZE_Y + endY * DEBUG_TILE + DEBUG_TILE / 2;

      this.entityGfx.lineStyle(2, C.spiderPatrol, 0.55);
      this.entityGfx.lineBetween(startCx, startCy, endCx, endCy);

      // Spider at initial offset position
      const sx =
        sp.axis === 'horizontal' ? sp.start.x + sp.initialOffset : sp.start.x;
      const sy =
        sp.axis === 'vertical' ? sp.start.y + sp.initialOffset : sp.start.y;
      this.entityGfx.fillStyle(C.spider, 1);
      this.entityGfx.fillCircle(
        MAZE_X + sx * DEBUG_TILE + DEBUG_TILE / 2,
        MAZE_Y + sy * DEBUG_TILE + DEBUG_TILE / 2,
        DEBUG_TILE * 0.36
      );
    }

    // Coconuts
    for (const c of pf.coconuts) {
      const cx = MAZE_X + c.x * DEBUG_TILE + DEBUG_TILE / 2;
      const cy = MAZE_Y + c.y * DEBUG_TILE + DEBUG_TILE / 2;
      const r = DEBUG_TILE * 0.23;

      if (!c.reachable) {
        // Flag unreachable coconuts in pink so we can spot generator bugs.
        this.entityGfx.fillStyle(C.coconutUnreachable, 1);
        this.entityGfx.fillCircle(cx, cy, r);
        this.entityGfx.lineStyle(1, 0xffffff, 0.8);
        this.entityGfx.strokeCircle(cx, cy, r);
      } else if (c.invisible) {
        // Invisible coconuts: faint fill + outline so we can still see them in debug.
        this.entityGfx.fillStyle(C.coconutInvisible, 0.35);
        this.entityGfx.fillCircle(cx, cy, r);
        this.entityGfx.lineStyle(1, C.coconutInvisible, 0.9);
        this.entityGfx.strokeCircle(cx, cy, r);
      } else {
        this.entityGfx.fillStyle(C.coconut, 1);
        this.entityGfx.fillCircle(cx, cy, r);
      }
    }

    // Spawn (chimp)
    const sx = MAZE_X + pf.spawn.x * DEBUG_TILE + DEBUG_TILE / 2;
    const sy = MAZE_Y + pf.spawn.y * DEBUG_TILE + DEBUG_TILE / 2;
    this.entityGfx.fillStyle(C.chimp, 1);
    this.entityGfx.fillCircle(sx, sy, DEBUG_TILE * 0.42);
    this.entityGfx.fillStyle(0x000000, 1);
    this.entityGfx.fillCircle(sx - DEBUG_TILE * 0.13, sy - DEBUG_TILE * 0.06, DEBUG_TILE * 0.07);
    this.entityGfx.fillCircle(sx + DEBUG_TILE * 0.13, sy - DEBUG_TILE * 0.06, DEBUG_TILE * 0.07);

    this.drawViewport();
    this.updateText();
  }

  private drawViewport(): void {
    this.viewportGfx.clear();
    if (!this.viewportVisible || !this.playfield) return;

    const pf = this.playfield;
    const vx = clamp(
      pf.spawn.x - Math.floor(VIEWPORT_TILES_W / 2),
      0,
      pf.width - VIEWPORT_TILES_W
    );
    const vy = clamp(
      pf.spawn.y - Math.floor(VIEWPORT_TILES_H / 2),
      0,
      pf.height - VIEWPORT_TILES_H
    );
    this.viewportGfx.lineStyle(2, C.viewport, 0.45);
    this.viewportGfx.strokeRect(
      MAZE_X + vx * DEBUG_TILE,
      MAZE_Y + vy * DEBUG_TILE,
      VIEWPORT_TILES_W * DEBUG_TILE,
      VIEWPORT_TILES_H * DEBUG_TILE
    );
  }

  private updateText(): void {
    const pf = this.playfield;
    const s = pf.stats;
    this.headerText.setText(
      `SEED: ${pf.seed}    GEN: ${s.generationMs}ms`
    );
    const invisible = pf.coconuts.filter((c) => c.invisible).length;
    const reachable = pf.coconuts.filter((c) => c.reachable).length;
    this.statsText.setText(
      `foliage:${s.foliageTiles}  water:${s.waterTiles}  spiders:${pf.spiders.length}(${s.spiderTiles}t)  ` +
        `coconuts:${pf.coconuts.length} [vis:${pf.coconuts.length - invisible} inv:${invisible} reach:${reachable}/${pf.coconuts.length}]  ` +
        `walkable:${s.connectedGroundTiles}/${s.totalGroundTiles}`
    );
  }
}

// --- helpers ---------------------------------------------------------------

function randomSeed(): string {
  return Math.random().toString(36).slice(2, 10);
}

function todaysSeed(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
